import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http } from "@/lib/data";
import { useMfMeta, pct, shortCat } from "@/lib/format";
import { GOALS } from "@/lib/goals";

export const FundPicker = ({ kind = "mf", onPick, exclude = [], compact = false }) => {
  const goals = GOALS[kind];
  const [goal, setGoal] = useState(goals[0].id);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const { data: meta } = useMfMeta();
  const counts = Object.fromEntries((meta?.categories || []).map((c) => [c.category, c.count]));
  const g = goals.find((x) => x.id === goal);
  const cats = g.cats.filter((c) => counts[c]);
  const active = cat || cats[0] || "";
  const { data: funds, isFetching } = useQuery({
    queryKey: ["picker", active], enabled: !!active,
    queryFn: () => http.get("/mf/funds", { params: { category: active, limit: 500, sort: "r3y" } }).then((r) => r.data.items),
  });

  return (
    <div data-testid={`fund-picker-${kind}`} className="space-y-4">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">1 · What's your goal?</p>
        <div className={`grid gap-2 ${compact ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-6"}`}>
          {goals.map(({ id, label, hint, icon: I }) => (
            <button key={id} type="button" data-testid={`goal-${id}`} onClick={() => { setGoal(id); setCat(""); setQ(""); }}
              className={`text-left rounded-xl border-2 p-3 transition-colors ${goal === id ? "border-[#10B981] bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <I className={`w-4 h-4 ${goal === id ? "text-[#059669]" : "text-amber-500"}`} />
              <span className="block font-bold text-sm mt-1.5 leading-tight">{label}</span>
              <span className="block text-[11px] text-slate-500">{hint}</span>
            </button>
          ))}
        </div>
      </div>
      <div className={`grid gap-3 ${compact ? "" : "md:grid-cols-2"}`}>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">2 · Fund type</p>
          <Select value={active} onValueChange={setCat}>
            <SelectTrigger data-testid="picker-category-select" className="h-12 rounded-xl bg-white"><SelectValue placeholder="Choose type" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{shortCat(c)} ({counts[c]})</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">3 · Pick a fund <span className="normal-case font-normal">(best 3Y first)</span></p>
          <input data-testid="picker-fund-filter" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name or AMC (optional)" className="w-full h-10 mb-2 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-[#10B981]" />
          <Select value="" onValueChange={(c) => onPick(c, funds.find((f) => f.code === c))}>
            <SelectTrigger data-testid="picker-fund-select" className="h-12 rounded-xl bg-white"><SelectValue placeholder={isFetching ? "Loading funds…" : `Choose from ${funds?.length || 0} funds`} /></SelectTrigger>
            <SelectContent className="max-h-80">
              {(funds || []).filter((f) => !exclude.includes(f.code) && `${f.name} ${f.amc}`.toLowerCase().includes(q.toLowerCase())).map((f) => (
                <SelectItem key={f.code} value={f.code} data-testid={`picker-fund-${f.code}`}>
                  <span className="font-semibold">{f.name}</span> <span className="text-xs text-slate-500">· 3Y {pct(f.r3y)}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};
